import { useCallback, useMemo } from "react";

import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/_ui/select";
import { RowData } from "@tanstack/react-table";

import { useDatatableContext } from "../use-datatable-context";

const NO_SUBSECTION_OPTION = "__NO_SUBSECTION__";

export function DatatableDisplayOptionsSubsectioningSelect<TData extends RowData>() {
  const { table, getConfigurationForColumn, setState, state } = useDatatableContext<TData>();
  const { sectionColumnId, subSectionColumnId } = state;

  const getDisplayValueForColumn = useCallback(
    (id: string) => {
      const columnConfiguration = getConfigurationForColumn(id);
      if (!columnConfiguration) {
        return "";
      }
      return columnConfiguration.getDisplayValue();
    },
    [getConfigurationForColumn],
  );

  const columns = useMemo(() => {
    return table
      .getAllColumns()
      .slice()
      .filter((column) => {
        if (column.id === sectionColumnId) {
          return false;
        }
        const columnConfiguration = getConfigurationForColumn(column.id);
        if (!columnConfiguration) {
          return false;
        }
        return columnConfiguration.enableSectioning;
      })
      .sort((a, b) => {
        const displayValueA = getDisplayValueForColumn(a.id).toLocaleLowerCase();
        const displayValueB = getDisplayValueForColumn(b.id).toLocaleLowerCase();
        return displayValueA.localeCompare(displayValueB);
      });
  }, [table, getDisplayValueForColumn, getConfigurationForColumn, sectionColumnId]);

  const onValueChange = (columnId: string) => {
    if (columnId === NO_SUBSECTION_OPTION) {
      setState({
        subSectionColumnId: null,
      });
    } else {
      setState({
        subSectionColumnId: columnId,
      });
    }
  };

  return (
    <div className="flex w-full flex-row items-center gap-1">
      <Select value={subSectionColumnId ?? ""} onValueChange={onValueChange}>
        <SelectTrigger className="h-6 px-2 text-xs ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0">
          <div className="flex flex-row items-center justify-between text-xs">
            {!subSectionColumnId && "Klicken um zu gruppieren"}
            {subSectionColumnId && <>{getDisplayValueForColumn(subSectionColumnId)}</>}
          </div>
        </SelectTrigger>
        <SelectContent className="p-0">
          {columns.map((column) => {
            return (
              <SelectItem
                key={column.id}
                value={column.id}
                className="flex h-6 flex-row items-center justify-between text-xs"
              >
                {getDisplayValueForColumn(column.id)}
              </SelectItem>
            );
          })}
          <SelectItem value={NO_SUBSECTION_OPTION} className="flex h-6 flex-row items-center justify-between text-xs">
            Keine Gruppierung
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
